import { useCallback, useContext, useMemo } from 'react';
import { AppMessagingContext } from './app-messaging-context';
import { AppMessage } from './types';

export const useAppMessaging = () => {
  const context = useContext(AppMessagingContext);

  if (!context) {
    throw new Error('useAppMessaging must be used within AppMessagingProvider');
  }

  const send = useCallback(
    (message: AppMessage) => {
      context.send(message);
    },
    [context],
  );

  const onMessage = useCallback(
    (callback: (message: AppMessage) => void) => {
      return context.onMessage((m) => {
        callback(m);
      });
    },
    [context],
  );

  const registerIframe = useCallback(
    (id: string, iframe: HTMLIFrameElement, origin?: string) => {
      context.registerIframe(id, iframe, origin);
    },
    [context],
  );

  const unregisterIframe = useCallback(
    (id: string) => {
      context.unregisterIframe(id);
    },
    [context],
  );

  const sendToChild = useCallback(
    (childId: string, message: AppMessage) => {
      context.sendToChild(childId, message);
    },
    [context],
  );

  const broadcastToChildren = useCallback(
    (message: AppMessage) => {
      context.broadcastToChildren(message);
    },
    [context],
  );

  return useMemo(
    () => ({
      mode: context.mode,
      isInitialised: context.isInitialised,
      send,
      onMessage,
      registerIframe,
      unregisterIframe,
      sendToChild,
      broadcastToChildren,
    }),
    [
      context.mode,
      context.isInitialised,
      send,
      onMessage,
      registerIframe,
      unregisterIframe,
      sendToChild,
      broadcastToChildren,
    ],
  );
};
